/* eslint-disable react/jsx-no-useless-fragment */
import { CategoryItemsProps, Template } from '@zeus/shared';
import { BoxComponent } from '../../../../../base/box/Box';
import { TypographyComponent } from '../../../../../base/typography/Typography';
import { memo, useEffect, useState } from 'react';
import { extractFontFamilyFromUrl, getScaleSize, isBetweenRange } from '../../../../../utils/utils';
import { useNormalizedPixels } from '../../../../../hooks/useResize';

interface Item extends CategoryItemsProps {
  hasValidModifiers: boolean;
}

interface ItemProps {
  template: Template;
  item: Item;
  development?: boolean;
  lastItemIndex: number;
  index: number;
  timezone?: string;
  scale?: number;
  hasAnyModifier: boolean;
  tenantId?: number;
}

const Item = ({ template, item, development, timezone, scale = 1 }: ItemProps) => {
  const [active, setActive] = useState<boolean | null>(true);
  const { normalizePixels } = useNormalizedPixels();

  useEffect(() => {
    if (development === false) {
      const intervalId = setInterval(() => {
        setActive(isBetweenRange(item.schedules, timezone));
      }, 10000);

      return () => clearInterval(intervalId);
    }
  }, []);

  const isItemPriceEnabled = template?.itemPrice && template.itemPrice.isEnabled;
  const isDevelopment = development ? true : isBetweenRange(item.schedules, timezone);
  const isDescription = template?.itemDescription?.isEnabled;
  const itemTextScale = template?.item?.textScale ?? 1;
  const fontFamily = extractFontFamilyFromUrl(template?.fontFamily);

  return (
    active && (
      <>
        {isDevelopment && (
          <BoxComponent
            key={item.id}
            sx={{
              backgroundColor: 'transparent',
              borderRadius: 0,
              textTransform: 'capitalize',
              flex: 1,
              minWidth: 0,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              position: 'relative',
              maxWidth: '22%',
            }}
          >
            {/* Image container - takes remaining space */}
            <BoxComponent
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
                overflow: 'hidden',
                ...(item.outOfStock ? { opacity: '50%' } : {}),
              }}
            >
              <img
                src={item.itemPicture}
                alt={item.itemName}
                style={{
                  width: '90%',
                  height: '100%',
                  borderRadius: `${normalizePixels(12, 'height')}px`,
                }}
              />
            </BoxComponent>
            {isItemPriceEnabled && (
              <BoxComponent
                sx={{
                  position: 'absolute',
                  top: normalizePixels(6, 'height'),
                  right: normalizePixels(8, 'width'),
                  backgroundColor: template?.item?.itemPrice?.backgroundColor
                    ? template?.item?.itemPrice?.backgroundColor
                    : '#840577',
                  borderRadius: '999px',
                  padding: `${normalizePixels(6, 'height')}px ${normalizePixels(18, 'width')}px`,
                  ...(item.outOfStock ? { opacity: '50%' } : {}),
                }}
              >
                <TypographyComponent
                  sx={{
                    ...style.itemPrice,
                    color: template?.item?.itemPrice?.color ? template?.item?.itemPrice?.color : '#FFFEFE',
                    fontSize: getScaleSize(normalizePixels(26 * itemTextScale, 'height'), scale),
                    fontFamily: fontFamily,
                    fontWeight: template.item.itemPrice?.fontWeight,
                    fontStyle: template.item.itemPrice?.fontStyle,
                    letterSpacing: template.item.itemPrice?.letterSpacing,
                    lineHeight: template.item.itemPrice?.lineHeight,
                  }}
                >
                  $ {Number(item.itemPrice).toFixed(2)}
                </TypographyComponent>
              </BoxComponent>
            )}

            {/* Item details - fixed at bottom */}
            <BoxComponent
              sx={{
                flexShrink: 0,
                padding: `${normalizePixels(20, 'height')}px ${normalizePixels(12, 'width')}px`,
                height: '30%',
                position: 'relative',
              }}
            >
              {item.outOfStock && (
                <BoxComponent
                  sx={{
                    position: 'absolute',
                    top: `${normalizePixels(4, 'height')}px`,
                    left: `${normalizePixels(12, 'width')}px`,
                    display: 'inline-block',
                    backgroundColor: '#840577',
                    color: '#fff',
                    textAlign: 'center',
                    borderRadius: `${normalizePixels(4, 'height')}px`,
                    padding: `${normalizePixels(3, 'height')}px ${normalizePixels(8, 'width')}px`,
                    fontSize: getScaleSize(normalizePixels(12.5, 'height'), scale),
                  }}
                >
                  Out of stock
                </BoxComponent>
              )}
              <TypographyComponent
                sx={{
                  ...style.itemName,
                  color: template.item.itemName.color ? template.item.itemName.color : '#840577',
                  minWidth: 0,
                  fontSize: getScaleSize(normalizePixels(36 * itemTextScale, 'height'), scale),
                  ...(item.outOfStock ? { opacity: '50%' } : {}),
                  fontFamily: fontFamily,
                  fontWeight: template.item.itemName.fontWeight,
                  fontStyle: template.item.itemName.fontStyle,
                  letterSpacing: template.item.itemName.letterSpacing,
                  lineHeight: template.item.itemName.lineHeight,
                  textAlign: 'center',
                }}
              >
                {item.itemName}
              </TypographyComponent>

              {isDescription && item.itemDescription && (
                <TypographyComponent
                  sx={{
                    ...style.itemDesc,
                    color: template.item?.itemDescription?.color ? template.item?.itemDescription?.color : '#000',
                    fontSize: getScaleSize(normalizePixels(18 * itemTextScale, 'height'), scale),
                    ...(item.outOfStock ? { opacity: '50%' } : {}),
                    fontFamily: fontFamily,
                    fontWeight: template.item.itemDescription?.fontWeight,
                    fontStyle: template.item.itemDescription?.fontStyle,
                    letterSpacing: template.item.itemDescription?.letterSpacing,
                    lineHeight: template.item.itemDescription?.lineHeight,
                    textAlign: 'center',
                  }}
                >
                  {item.itemDescription}
                </TypographyComponent>
              )}
            </BoxComponent>
          </BoxComponent>
        )}
      </>
    )
  );
};

export default memo(Item);

const style = {
  container: {
    alignSelf: 'start',
    height: 'min-content',
    overflow: 'hidden',
  },
  itemNameContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  itemName: {
    fontWeight: '600',
    fontFamily: 'Manrope, sans-serif',
    '-webkit-line-clamp': '1',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  itemPrice: {
    textAlign: 'center',
    fontFamily: 'Manrope, sans-serif',
    lineHeight: 'normal',
    fontWeight: '600',
  },
  itemDesc: {
    fontWeight: '400',
    display: '-webkit-box',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
  },
};
